import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Share2, Plus, Trash2, Send, Pencil, Clock, Calendar,
  Loader2, Facebook, Twitter, Instagram, Linkedin,
  Heart, MessageCircle, Repeat2, MousePointer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: "facebook", label: "Facebook", icon: Facebook, color: "text-blue-600" },
  { id: "twitter", label: "Twitter / X", icon: Twitter, color: "text-sky-500" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "text-pink-500" },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "text-blue-700" },
];

const statusBadge = {
  draft: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  scheduled: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  failed: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
};

export default function SocialMedia() {
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [activeTab, setActiveTab] = useState("posts");
  const [form, setForm] = useState({
    content: "", platforms: [], image_url: "", link_url: "", scheduled_at: "", status: "draft",
  });

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/social/posts`),
        axios.get(`${API}/social/stats`),
      ]);
      setPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ content: "", platforms: [], image_url: "", link_url: "", scheduled_at: "", status: "draft" });
    setEditing(null);
  };

  const togglePlatform = (pid) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(pid)
        ? f.platforms.filter(p => p !== pid)
        : [...f.platforms, pid]
    }));
  };

  const handleSave = async () => {
    if (!form.content.trim()) { toast.error(t("social.enterContent")); return; }
    if (form.platforms.length === 0) { toast.error(t("social.selectPlatform")); return; }
    try {
      if (editing) {
        await axios.put(`${API}/social/posts/${editing.id}`, form);
      } else {
        await axios.post(`${API}/social/posts`, form);
      }
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false); resetForm();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEdit = (post) => {
    setEditing(post);
    setForm({
      content: post.content, platforms: post.platforms || [],
      image_url: post.image_url || "", link_url: post.link_url || "",
      scheduled_at: post.scheduled_at || "", status: post.status,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/social/posts/${id}`); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  const handlePublish = async (id) => {
    setPublishing(id);
    try { await axios.post(`${API}/social/posts/${id}/publish`); toast.success(t("social.published")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
    finally { setPublishing(null); }
  };

  const charCount = form.content.length;
  const charLimit = form.platforms.includes("twitter") ? 280 : 2200;

  return (
    <div data-testid="social-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("social.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("social.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-social-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("social.createPost")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? t("sites.edit") : t("social.createPost")}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {/* Platformy */}
              <div className="space-y-2">
                <Label className="text-xs">{t("social.platforms")}</Label>
                <div className="flex gap-2">
                  {PLATFORMS.map((p) => (
                    <button key={p.id} data-testid={`platform-${p.id}`} onClick={() => togglePlatform(p.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                        form.platforms.includes(p.id) ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"
                      }`}>
                      <p.icon className={`w-3.5 h-3.5 ${form.platforms.includes(p.id) ? p.color : ""}`} strokeWidth={1.5} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Tresc */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">{t("social.content")}</Label>
                  <span className={`text-[10px] ${charCount > charLimit ? "text-red-500" : "text-muted-foreground"}`}>
                    {charCount}/{charLimit}
                  </span>
                </div>
                <Textarea data-testid="social-content" value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={4} className="resize-none" placeholder={t("social.contentPlaceholder")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("social.imageUrl")}</Label>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("social.linkUrl")}</Label>
                  <Input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              {/* Planowanie */}
              <div className="space-y-1.5">
                <Label className="text-xs">{t("social.scheduleAt")}</Label>
                <Input type="datetime-local" data-testid="schedule-input" value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("sites.cancel")}</Button>
              <Button data-testid="save-social-btn" onClick={handleSave}>{t("sites.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t("social.totalPosts"), value: stats.total, color: "text-slate-500" },
            { label: t("social.scheduled"), value: stats.scheduled, color: "text-amber-500" },
            { label: t("social.publishedCount"), value: stats.published, color: "text-emerald-500" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 text-center">
                <p className={`text-xl font-bold font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3">
              <div className="flex items-center justify-center gap-3">
                {PLATFORMS.map((p) => (
                  <div key={p.id} className="text-center">
                    <p.icon className={`w-4 h-4 mx-auto ${p.color}`} strokeWidth={1.5} />
                    <p className="text-xs font-bold text-foreground mt-0.5">{stats.by_platform?.[p.id] || 0}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista postow */}
      <Card className="rounded-xl border shadow-sm bg-card">
        <CardContent className="p-0">
          {posts.length === 0 ? (
            <div data-testid="no-social" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Share2 className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
              <p className="text-xs">{t("social.noPosts")}</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[500px]">
              <div className="divide-y divide-border">
                {posts.map((post) => (
                  <div key={post.id} data-testid={`social-${post.id}`} className="p-4 hover:bg-accent/20 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground leading-relaxed">{post.content.substring(0, 200)}{post.content.length > 200 ? "..." : ""}</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {(post.platforms || []).map((pid) => {
                            const pl = PLATFORMS.find(p => p.id === pid);
                            return pl ? (
                              <Badge key={pid} variant="outline" className="text-[9px] rounded-md gap-0.5">
                                <pl.icon className={`w-2.5 h-2.5 ${pl.color}`} strokeWidth={1.5} />{pl.label}
                              </Badge>
                            ) : null;
                          })}
                          <Badge className={`text-[9px] border-0 rounded-md ${statusBadge[post.status] || statusBadge.draft}`}>
                            {post.status}
                          </Badge>
                          {post.scheduled_at && (
                            <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />{new Date(post.scheduled_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {/* Metryki */}
                        {post.status === "published" && post.metrics && (
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Heart className="w-3 h-3" />{post.metrics.likes}
                            </span>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Repeat2 className="w-3 h-3" />{post.metrics.shares}
                            </span>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <MessageCircle className="w-3 h-3" />{post.metrics.comments}
                            </span>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <MousePointer className="w-3 h-3" />{post.metrics.clicks}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {post.status !== "published" && (
                          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 text-emerald-600"
                            onClick={() => handlePublish(post.id)} disabled={publishing === post.id}>
                            {publishing === post.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" strokeWidth={1.5} />}
                            {t("social.publish")}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(post)}>
                          <Pencil className="w-3 h-3" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(post.id)}>
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
